import React from "react";
import { observer } from "mobx-react";
import styles from "./OverheadTypesList.module.scss";
import { columns } from "./columns";
import { saveAgGridColumnSize } from "modules/common/services/table/size-storage";
import { defaultModules, DefaultAgGridOptions } from "modules/common/services/table/helpers";
import { RemoveConfirmation } from "modules/common/components/form/RemoveConfirmation";
import { agGridEntityContextMenu } from "modules/common/components/table/EntityContextMenu";
import { OverheadTypeList, OverheadTypesListType } from "../../models/overhead-type-list";
import { OverheadTypeSnapshotType, formatOverheadType } from "../../models/overhead-type";
import copyToClipboard from "copy-to-clipboard";

import { MAX_INT } from "modules/common/constants";
import { MenuModule } from "@ag-grid-enterprise/menu";
import {
    GridOptions,
    GetContextMenuItemsParams,
    CellContextMenuEvent,
    RowClickedEvent,
    GridReadyEvent,
} from "@ag-grid-community/core";
import { GridTheme } from "modules/common/components/table/GridTheme";
import { AgGridReact } from "@ag-grid-community/react/lib/agGridReact";
import { agGridLocale } from "modules/root/services/locale";

const modules = [...defaultModules, MenuModule];
const TABLE_NAME = OverheadTypeList.name;
const sizeSaver = saveAgGridColumnSize(TABLE_NAME);
const config = columns(TABLE_NAME);

export const OverheadTypesList = observer(
    class extends React.Component<OverheadTypesListProps> {
        private options: GridOptions = DefaultAgGridOptions(true);

        render() {
            const { store, onSelected } = this.props;

            return (
                <RemoveConfirmation<OverheadTypeSnapshotType>
                    observation={store.data}
                    what={this.removeLabel}
                    onConfirmed={this.removeStatus}
                    render={({ confirmRemoving }) => {
                        const getContextMenuItems = ({ node }: GetContextMenuItemsParams) =>
                            agGridEntityContextMenu({
                                onCopy: () => copyToClipboard(formatOverheadType(node.data), { format: "text/plain" }),
                                onChange: () => onSelected(getRowNodeId(node.data)),
                                onRemove: () => confirmRemoving(node.data),
                            });

                        return (
                            <GridTheme bordered={true} className={`designed ${styles.grid}`}>
                                <AgGridReact
                                    rowStyle={{
                                        borderBottom: "1 px solid #EDF0F2",
                                        fontFamily: "GothamPro, Arial, sans-serif",
                                        color: "#00273D",
                                        height: "32px",
                                        fontSize: "12px",
                                    }}
                                    rowSelection="single"
                                    suppressCellSelection={true}
                                    enableCellTextSelection={true}
                                    columnDefs={config}
                                    rowData={store.data}
                                    localeText={agGridLocale}
                                    onRowDoubleClicked={this.onRowSelected}
                                    getRowNodeId={getRowNodeId}
                                    getContextMenuItems={getContextMenuItems}
                                    onGridReady={this.onGridReady}
                                    onCellContextMenu={this.onCellContextMenu}
                                    onSortChanged={store.sorter.resorted}
                                    onColumnResized={sizeSaver}
                                    gridOptions={this.options}
                                    modules={modules}
                                    rowBuffer={MAX_INT}
                                />
                            </GridTheme>
                        );
                    }}
                />
            );
        }

        removeLabel = (type: OverheadTypeSnapshotType) => `тип накладного расхода ${type.name}`;

        removeStatus = async (status: OverheadTypeSnapshotType) => {
            const { store, onRemoved } = this.props;
            const success = await store.delete(status);
            success && onRemoved();
        };

        onCellContextMenu = ({ node }: CellContextMenuEvent) => {
            node && node.setSelected(true, true);
        };

        onRowSelected = ({ data }: RowClickedEvent) => {
            const { onSelected } = this.props;
            onSelected(getRowNodeId(data));
        };

        onGridReady = async ({ api }: GridReadyEvent) => {
            const { sorter } = this.props.store;
            setTimeout(() => api.setSortModel(sorter.agGridSortModel));
        };
    }
);

const getRowNodeId = (e: OverheadTypeSnapshotType) => e.id;

interface OverheadTypesListProps {
    onRemoved: () => void;
    onSelected: (id: string) => void;
    store: OverheadTypesListType;
}
